import React, { useEffect, useState } from 'react';
import { View, Image, TouchableOpacity, StyleSheet, ScrollView, SafeAreaView, Platform, useWindowDimensions } from 'react-native';
import Text from '../../components/text';
import getPlatformType from '../../helpers/helpers';
import { AntDesign, Feather, MaterialIcons, Ionicons } from '@expo/vector-icons';
import ajax from '../../helpers/ajaxHelper';
import { useRoute } from '@react-navigation/native';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import { showErrorToast } from '../tenants/tenantOnboarding/helpers';
import FilePicker from '../../components/filePicker/filepicker.web';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import useUtilsProvider from '../../hooks/useUtilsProvider';
import Urls from '../../services/Urls';
import { LAYOUT } from '../../constants/theme';
import { SetupSidebar, usePropertyRouteParams, PropertyBreadcrumb, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

/* =========================================================
   PLATFORM
========================================================= */
const layoutType = getPlatformType();
const isWeb = layoutType === 'web';
const isTab = layoutType === 'tab';
const isPhone = layoutType === 'phone';

/* =========================================================
   ROOM COUNTER  (matches Figma: Name  -  0  +)
========================================================= */
const ROOM_ITEMS = [
  'Bedroom', 'Bathroom', 'Kitchen', 'Living Room',
  'Dining Room', 'Utility Area', 'Gym', 'Basement',
  'Conservatory', 'Garage', 'Office', 'Other',
];

const RoomCounter = ({ values, onChange }) => {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (values && typeof values === 'object') setCounts(values);
  }, [values]);

  const update = (item, delta) => {
    const next = { ...counts, [item]: Math.max(0, (counts[item] || 0) + delta) };
    setCounts(next);
    onChange(next);
  };

  // On phone: 2 columns. On tablet/web: 4 columns
  const colWidth = isPhone ? '50%' : '25%';

  return (
    <View style={rc.grid}>
      {ROOM_ITEMS.map((item) => (
        <View key={item} style={[rc.cell, { width: colWidth }]}>
          <Text style={rc.name} numberOfLines={1}>{item}</Text>
          <View style={rc.controls}>
            <TouchableOpacity onPress={() => update(item, -1)} style={rc.btn}>
              <Text style={rc.btnTxt}>−</Text>
            </TouchableOpacity>
            <Text style={rc.countTxt}>{counts[item] || 0}</Text>
            <TouchableOpacity onPress={() => update(item, 1)} style={rc.btn}>
              <Text style={rc.btnTxt}>+</Text>
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </View>
  );
};

const rc = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 11,
    paddingHorizontal: isPhone ? 6 : 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f2f2f7',
  },
  name: { fontSize: 13, color: '#222', fontWeight: '500', flex: 1, marginRight: 4 },
  controls: { flexDirection: 'row', alignItems: 'center' },
  btn: {
    width: 26, height: 26, borderRadius: 13,
    backgroundColor: '#f0ecff',
    alignItems: 'center', justifyContent: 'center',
  },
  btnTxt: { fontSize: 18, color: '#6D08C0', fontWeight: '700', lineHeight: 22 },
  countTxt: { fontSize: 14, color: '#1a1a2e', fontWeight: '700', minWidth: 22, textAlign: 'center' },
});

/* =========================================================
   UPLOAD ZONE
========================================================= */
const UploadZone = ({ label, helpText, images, onAdd, onRemove, accept = "image/*" }) => (
  <View style={s.uploadBox}>
    <Text style={s.sectionLabel}>{label}</Text>
    <View style={s.dropZone}>
      <View style={s.dropIcon}>
        <Feather name="camera" size={20} color="#6D08C0" />
      </View>
      <FilePicker
        accept={accept}
        style={{ width: '100%' }}
        onDataSuccess={(data) => { if (data?.length) onAdd(data); }}
      />
      <Text style={s.dropPrimary}>{helpText}</Text>
      <Text style={s.dropSecondary}>Supported: JPG, PNG{accept.includes('pdf') ? ', PDF' : ''}</Text>
    </View>
    {images?.length > 0 && (
      <View style={s.thumbRow}>
        {images.map((img, idx) => (
          <View key={idx} style={s.thumb}>
            {img.openbrixName?.toLowerCase().endsWith('.pdf') ? (
              <View style={[s.thumbImg, { justifyContent: 'center', alignItems: 'center', backgroundColor: '#e0e0e0' }]}>
                <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#6D08C0' }}>PDF</Text>
              </View>
            ) : (
              <Image source={{ uri: img.openbrixName }} style={s.thumbImg} />
            )}
            <TouchableOpacity style={s.thumbRemove} onPress={() => onRemove(idx)}>
              <AntDesign name="closecircle" size={16} color="#fff" />
            </TouchableOpacity>
          </View>
        ))}
      </View>
    )}
  </View>
);

/* =========================================================
   SECTION CARD WRAPPER
========================================================= */
const Section = ({ label, children }) => (
  <View style={s.sectionCard}>
    {label ? <Text style={s.sectionLabel}>{label}</Text> : null}
    {children}
  </View>
);

/* =========================================================
   MAIN COMPONENT
========================================================= */
const PropertyDetails = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const propId = routeParams.id;
  const propertyName = routeParams.name || 'Property';
  const propertyId = propId ? `HBT-${String(propId).padStart(4, '0')}-2024` : '';
  const { width } = useWindowDimensions();
  const isMobile = width <= 1024;
  const [state, setState] = useState({
    images: [],
    floor_plan_images: [],
    description: '',
    quantity: {},
    property_type: '',
    available_date: '',
    rent_amount: '',
    deposit_amount: '',
    price_worth: '',
    council_tax_band: '',
    epc_rating: '',
    furnished: '',
    pet_friendly: '',
  });

  const [selectedProviders, setSelectedProviderData] = useState([]);
  const { response } = useUtilsProvider(Urls.GET_PROVIDERS);

  useEffect(() => { }, [response]);

  const set = (key, val) => setState((prev) => ({ ...prev, [key]: val }));

  const _onSave = () => {
    if (!propId) {
      showErrorToast('Property ID missing');
      return;
    }
    const requestBody = {
      images: state.images || [],
      floor_plan_images: state.floor_plan_images || [],
      price_worth: state.price_worth || null,
      quantity: state.quantity || {},
      description: state.description || null,
      providers: selectedProviders || [],
    };
    ajax
      .post(Urls.ROOMS + '/' + propId, requestBody)
      .then(({ data }) => {
        if (data.status) {
          navigation.navigate('PropertySection', { id: propId, name: propertyName });
        } else {
          showErrorToast(data?.message || 'Failed to save property details');
        }
      })
      .catch((err) => {
        console.log('error saving property details', err);
        showErrorToast('Error saving property details');
      });
  };

  useEffect(() => {
    if (propId) {
      ajax
        .get(Urls.Properties + '/' + propId)
        .then(({ data }) => {
          if (data.status) {
            setState((prev) => ({
              ...prev,
              images: data?.payload?.images?.length ? data.payload.images : [],
              floor_plan_images: data?.payload?.floor_plan_images?.length ? data.payload.floor_plan_images : [],
              quantity: data?.payload?.quantity || {},
              description: data?.payload?.description || '',
              price_worth: data?.payload?.price_worth || '',
              rent_amount: data?.payload?.rent_amount || '',
              deposit_amount: data?.payload?.deposit_amount || '',
              property_type: data?.payload?.property_type || '',
              council_tax_band: data?.payload?.council_tax_band || '',
              epc_rating: data?.payload?.epc_rating || '',
              furnished: data?.payload?.furnished || '',
            }));
            if (data?.payload?.providerDetails?.length)
              setSelectedProviderData(data.payload.providerDetails);
          } else {
            showErrorToast(data.message);
          }
        })
        .catch((err) => console.log('error', err));
    }
  }, [propId]);

  return (
    <View style={{ flex: 1, position: 'relative' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <PropertyBreadcrumb
          navigation={navigation}
          propertyId={propId}
          propertyName={propertyName}
          currentStep="Property Details"
        />

        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="home-outline"
          title="Property Details"
          subtitle="Configure rooms, amenities and upload property photos."
          isMobile={isMobile}
          rightComponent={
            propId ? (
              <MarkAsComplete
                flowID={propId}
                flowtype={routeParams.key || 'room_details_status'}
                isComplete={routeParams.status === 2}
              />
            ) : null
          }
        />

        {/* 2-Column Main Layout: Form (Left) & Setup Progress Sidebar (Right) */}
        <View style={s.bodyWeb}>
          {/* Main Form Column */}
          <View style={[s.mainCard, s.mainCardWeb]}>
            {/* ROOM TYPES */}
            <View style={s.formSection}>
              <Text style={s.sectionLabel}>ROOM TYPES</Text>
              <RoomCounter values={state.quantity} onChange={(val) => set('quantity', val)} />
            </View>

            {/* PHOTOS + FLOOR PLAN side by side */}
            <View style={s.formSection}>
              <View style={s.uploadRow}>
                <UploadZone
                  label="PROPERTY PHOTOS"
                  helpText="Drag and drop files or click to upload"
                  images={state.images}
                  onAdd={(data) => set('images', [...(state.images || []), ...data])}
                  onRemove={(idx) => {
                    const arr = [...(state.images || [])];
                    arr.splice(idx, 1);
                    set('images', arr);
                  }}
                />
                <UploadZone
                  label="FLOOR PLAN"
                  helpText="Upload property floor plan"
                  accept="image/*, application/pdf"
                  images={state.floor_plan_images}
                  onAdd={(data) => set('floor_plan_images', [...(state.floor_plan_images || []), ...data])}
                  onRemove={(idx) => {
                    const arr = [...(state.floor_plan_images || [])];
                    arr.splice(idx, 1);
                    set('floor_plan_images', arr);
                  }}
                />
              </View>
            </View>

            {/* PROPERTY WORTH */}
            <View style={s.formSection}>
              <Text style={s.sectionLabel}>PROPERTY WORTH (£)</Text>
              <TextInputComponent
                placeholder="e.g. 350000"
                isAmount={true}
                onChangeText={(v) => set('price_worth', v)}
                value={state.price_worth ? String(state.price_worth) : ''}
              />
            </View>

            {/* DESCRIPTION */}
            <View style={s.formSection}>
              <Text style={s.sectionLabel}>PROPERTY DESCRIPTION</Text>
              <TextInputComponent
                height={120}
                maxLength={5000}
                multiline
                placeholder="Describe your property, highlighting key features and amenities..."
                onChangeText={(v) => set('description', v)}
                value={state.description}
              />
            </View>

            {/* PCID */}
            {propertyId ? (
              <View style={s.formSection}>
                <Text style={s.sectionLabel}>PROPERTY PCID</Text>
                <View style={s.pcidBox}><Text style={s.pcidText}>{propertyId}</Text></View>
              </View>
            ) : null}
          </View>

          {/* Setup progress sidebar */}
          {!isMobile ? (
            <SetupSidebar
              activeKey="property"
              propertyId={propId}
              propertyName={propertyName}
              navigation={navigation}
            />
          ) : null}
        </View>
      </ScrollView>

      {/* Fixed footer */}
      <PropertyStickyFooter onSave={_onSave} isMobile={isMobile} />
    </View>
  );
};

/* =========================================================
   STYLES
========================================================= */
const s = StyleSheet.create({
  /* ── MOBILE ──────────────────────────────────── */
  mobileRoot: {
    flex: 1,
    backgroundColor: '#f5f5f9',
  },
  mobileNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  mobileBack: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f5f5f9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileNavTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1a1a2e',
    flex: 1,
    textAlign: 'center',
  },
  mobileStepBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  mobileStepPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#f0e8ff',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  mobileStepDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#6D08C0',
  },
  mobileStepText: {
    fontSize: 12,
    color: '#6D08C0',
    fontWeight: '700',
  },
  mobileStepRight: {
    fontSize: 12,
    color: '#888',
  },
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    padding: 16,
    paddingBottom: 30,
  },
  mobilePropBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#f0e8ff',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignSelf: 'flex-start',
    marginBottom: 14,
  },
  mobilePropName: {
    fontSize: 12,
    color: '#6D08C0',
    fontWeight: '600',
  },
  mobileCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  mobileCardIcon: {
    width: 38,
    height: 38,
    borderRadius: 9,
    backgroundColor: '#f0e8ff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1a1a2e',
  },
  mobileCardSub: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
    lineHeight: 17,
  },
  mobileFooter: {
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.07,
    shadowRadius: 6,
    elevation: 10,
  },
  mobileFooterBtn: {
    backgroundColor: '#6D08C0',
    borderRadius: 30,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileFooterBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },

  /* ── SHARED SECTION CARD ─────────────────────── */
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    marginTop: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#999',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 10,
  },

  /* ── UPLOAD ZONE ─────────────────────────────── */
  uploadBox: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  dropZone: {
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: '#d0b8f0',
    borderRadius: 10,
    paddingVertical: 22,
    paddingHorizontal: 16,
    alignItems: 'center',
    backgroundColor: '#faf7ff',
    overflow: 'hidden',
  },
  dropIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#f0e8ff',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  dropPrimary: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    marginTop: 4,
  },
  dropSecondary: {
    fontSize: 11,
    color: '#aaa',
    textAlign: 'center',
    marginTop: 3,
  },
  thumbRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
  thumb: {
    width: 68,
    height: 68,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  thumbImg: { width: '100%', height: '100%' },
  thumbRemove: {
    position: 'absolute',
    top: 3,
    right: 3,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 10,
  },

  /* ── PCID ────────────────────────────────────── */
  pcidBox: {
    borderWidth: 1,
    borderColor: '#e0d5f5',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#faf7ff',
  },
  /* ── PROPERTY WORTH ──────────────────────────── */
  priceWorthBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    paddingLeft: 14,
  },
  priceWorthSymbol: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6B7280',
    marginRight: 4,
  },

  /* ── WEB / TABLET ────────────────────────────── */
  root: {
    flex: 1,
    backgroundColor: '#f4f4f8',
  },
  topBar: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 8,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginRight: 4,
  },
  backText: { fontSize: 13, color: '#555' },
  breadSep: { fontSize: 13, color: '#888' },
  stepBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    marginBottom: 20,
  },
  stepBarText: { fontSize: 13, color: '#555', fontWeight: '500' },
  currentStepBadge: {
    backgroundColor: '#f0e8ff',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},
  body: { paddingHorizontal: 16 },
  bodyWeb: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
    paddingHorizontal: 20,
  },
  mainCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
    marginBottom: 16,
  },
  mainCardWeb: { flex: 1, padding: 24, marginBottom: 0 },
  stickyFooter: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 8,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f5',
  },
  cardHeaderIcon: {
    width: 42, height: 42, borderRadius: 10,
    backgroundColor: '#f0e8ff',
    alignItems: 'center', justifyContent: 'center',
  },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#1a1a2e' },
  cardSub: { fontSize: 13, color: '#888', marginTop: 3 },
  formSection: { marginBottom: 24 },
  uploadRow: { flexDirection: 'row', gap: 16 },
  footer: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: 0, left: 0, right: 0,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
    zIndex: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 8,
  },
  footerNote: { fontSize: 13, color: '#888' },
  footerBtn: {
    backgroundColor: '#6D08C0',
    borderRadius: 30,
    paddingHorizontal: 22,
    paddingVertical: 12,
  },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },

  /* ── SIDEBAR ─────────────────────────────────── */
  sidebar: { width: 260 },
  sidebarCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  sidebarTitle: { fontSize: 15, fontWeight: '700', color: '#1a1a2e' },
  sidebarSub: { fontSize: 12, color: '#999', marginTop: 3, marginBottom: 18 },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 10,
    padding: 10,
    borderRadius: 10,
  },
  stepActive: { backgroundColor: '#f5eeff' },
  dotDone: {
    width: 26, height: 26, borderRadius: 13,
    backgroundColor: '#22c55e',
    alignItems: 'center', justifyContent: 'center', marginTop: 2,
  },
  dotActive: {
    width: 26, height: 26, borderRadius: 13,
    backgroundColor: '#6D08C0',
    alignItems: 'center', justifyContent: 'center', marginTop: 2,
  },
  stepName: { fontSize: 13, fontWeight: '600', color: '#333' },
  stepDone: { fontSize: 11, color: '#22c55e', fontWeight: '600', marginTop: 2 },
  stepCurrent: { fontSize: 11, color: '#6D08C0', fontWeight: '600', marginTop: 2 },
  sidebarHint: { fontSize: 11, color: '#aaa', marginTop: 10, textAlign: 'center' },
});

export default drawerHoc({
  Component: PropertyDetails,
  noScroll: true,
});
